import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { MessageCircle, Rocket, ChevronDown, ExternalLink, Users, Leaf } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const SIGNUP_LINKS = [
  { name: "Facebook", url: "https://www.facebook.com/r.php" },
  { name: "TikTok", url: "https://www.tiktok.com/signup" },
  { name: "Instagram", url: "https://www.instagram.com/accounts/emailsignup/" },
  { name: "WhatsApp", url: "https://www.whatsapp.com/download" },
];

const StepCard = ({ n, title, children }) => (
  <div className="rounded-xl border border-white/10 bg-black/25 p-4 sm:p-5">
    <div className="flex items-center gap-3 mb-2">
      <span className="w-8 h-8 shrink-0 rounded-full bg-[#FACC15] text-[#022C22] font-black flex items-center justify-center">{n}</span>
      <h3 className="font-bold text-[#F8FAF8]">{title}</h3>
    </div>
    <div className="text-[13px] text-emerald-100/80 leading-relaxed sm:pl-11">{children}</div>
  </div>
);

export const RenalifeWelcome = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showSignup, setShowSignup] = useState(false);

  return (
    <div
      data-testid="renalife-welcome"
      className="mb-8 rounded-2xl border border-[#FACC15]/30 p-6 sm:p-8"
      style={{ background: "linear-gradient(135deg, #022C22 0%, #064E3B 100%)" }}
    >
      <div className="flex items-center gap-2 font-renalife font-bold text-2xl sm:text-3xl text-[#F8FAF8]">
        <Leaf className="text-[#FACC15]" size={26} />
        ¡Bienvenido, distribuidor <span className="text-[#FACC15]">RENALIFE</span>!
      </div>
      <p className="text-sm text-emerald-100/80 mt-2 max-w-2xl">
        Tu campaña de 15 días <strong>ya está diseñada por expertos</strong>: imágenes, videos con voz, textos y tus
        páginas personales. Tú solo publicas con un toque. Sigue estos 3 pasos:
      </p>

      <div className="space-y-3 mt-5">
        <StepCard n={1} title="Configura tu WhatsApp">
          Ahí te escribirán las personas interesadas.{" "}
          {user?.whatsapp ? (
            <span className="text-green-300 font-semibold">✓ Ya lo configuraste ({user.whatsapp}).</span>
          ) : (
            <button data-testid="welcome-goto-profile" onClick={() => navigate("/dashboard/perfil")} className="underline text-[#FACC15] font-semibold">
              Configúralo aquí (1 minuto) →
            </button>
          )}
        </StepCard>

        <StepCard n={2} title="Ten listas tus cuentas personales">
          Publicarás desde tus propias cuentas de <strong>WhatsApp, Facebook y TikTok</strong> (así tus conocidos confían más).
          <button
            data-testid="welcome-no-accounts"
            onClick={() => setShowSignup(!showSignup)}
            className="flex items-center gap-1 mt-2 text-[#FACC15] font-semibold underline"
          >
            ¿No tienes alguna cuenta? Te decimos cómo crearla gratis
            <ChevronDown size={14} className={`transition-transform ${showSignup ? "rotate-180" : ""}`} />
          </button>
          {showSignup && (
            <div data-testid="welcome-signup-links" className="mt-3 rounded-lg border border-[#FACC15]/25 bg-[#FACC15]/5 p-3 space-y-2">
              <p className="text-xs text-emerald-100/80">
                1) Toca la red que te falte · 2) Regístrate con tu correo o celular (es gratis, ~2 minutos) ·
                3) <strong>Regresa aquí</strong> y continúa con el paso 3.
              </p>
              <div className="flex flex-wrap gap-2">
                {SIGNUP_LINKS.map((s) => (
                  <a
                    key={s.name}
                    href={s.url}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`signup-link-${s.name.toLowerCase()}`}
                    className="inline-flex items-center gap-1.5 text-xs font-bold rounded-full border border-white/20 bg-black/30 text-zinc-100 px-3.5 py-2 hover:border-[#FACC15]"
                  >
                    Crear cuenta {s.name} <ExternalLink size={11} />
                  </a>
                ))}
              </div>
            </div>
          )}
        </StepCard>

        <StepCard n={3} title='Haz clic en "INICIAR CAMPAÑA"'>
          La IA te entrega tus publicaciones de 15 días listas (con horarios recomendados) y cada día las compartes con un toque.
        </StepCard>
      </div>

      <div className="flex flex-wrap gap-3 mt-6">
        <button
          data-testid="renalife-start-campaign"
          onClick={() => navigate("/dashboard/calendario-renalife")}
          className="inline-flex items-center gap-2 bg-[#FACC15] hover:bg-yellow-300 text-[#022C22] font-black rounded-xl px-7 py-3.5 text-base transition-colors"
        >
          <Rocket size={18} /> INICIAR CAMPAÑA (15 días)
        </button>
        <button
          data-testid="renalife-view-materials"
          onClick={() => navigate("/dashboard/tiktok-renalife")}
          className="border border-[#FACC15]/50 text-[#FACC15] hover:bg-[#FACC15]/10 font-bold rounded-xl px-5 py-2.5 text-sm transition-colors"
        >
          TikTok con mi Avatar
        </button>
        <button
          data-testid="renalife-view-leads"
          onClick={() => navigate("/dashboard/prospectos")}
          className="inline-flex items-center gap-1.5 border border-white/15 text-zinc-300 hover:bg-white/5 font-semibold rounded-xl px-5 py-2.5 text-sm transition-colors"
        >
          <Users size={15} /> Mis prospectos
        </button>
      </div>
      <p className="text-[11px] text-emerald-100/60 mt-4 flex items-center gap-1.5">
        <MessageCircle size={12} /> Los interesados que dejen sus datos te llegarán a WhatsApp y a la sección "Prospectos".
      </p>
    </div>
  );
};
